import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Gauge,
  VolumeX,
  Check,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import { useLectureStore, PLAYBACK_RATES, RATE_MIN, RATE_MAX, RATE_STEP } from '../store/useLectureStore';

interface AudioControllerProps {
  // Called (throttled) to persist the resume position to the backend.
  onPersistPosition: (slideIndex: number, positionMs: number) => void;
  // Called when the current slide's audio finishes (covers it; may auto-advance).
  onAudioEnded: (slideIndex: number) => void;
  // The resume position (seconds) to seek to once, after metadata loads. Cleared
  // by the parent after the first slide so manual nav starts at 0.
  resumeSeconds?: number;
}

function fmt(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Seconds the ←/→ arrows scrub within the current track.
const SEEK_STEP = 5;

// HTML <audio> transport bound to the current slide's pre-synthesized R2 mp3.
// Provides play/pause, a precise scrubber with buffered-range + drag preview,
// elapsed/total, playback-rate menu, buffering state, keyboard shortcuts,
// next-slide precache, and on-end auto-advance honouring advance_rule.
export default function AudioController({
  onPersistPosition,
  onAudioEnded,
  resumeSeconds,
}: AudioControllerProps) {
  const slides = useLectureStore((s) => s.slides);
  const currentSlideIndex = useLectureStore((s) => s.currentSlideIndex);
  const isPlaying = useLectureStore((s) => s.isPlaying);
  const playbackRate = useLectureStore((s) => s.playbackRate);
  const setPlaying = useLectureStore((s) => s.setPlaying);
  const setPosition = useLectureStore((s) => s.setPosition);
  const setPlaybackRate = useLectureStore((s) => s.setPlaybackRate);
  const nextSlide = useLectureStore((s) => s.nextSlide);
  const prevSlide = useLectureStore((s) => s.prevSlide);
  const audioForSlide = useLectureStore((s) => s.audioForSlide);
  const completedSlides = useLectureStore((s) => s.completedSlides);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const preloadRef = useRef<HTMLAudioElement | null>(null);
  const lastPersistRef = useRef(0);
  const seekTargetRef = useRef<number | null>(null);
  // Set when an on_audio_end slide auto-advances, so the next slide's audio
  // starts playing automatically once its metadata is ready (continuous lecture
  // playback). Cleared after it fires.
  const autoContinueRef = useRef(false);

  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rateMenuOpen, setRateMenuOpen] = useState(false);
  // True between a stall/waiting event and the next playable frame — drives the
  // buffering spinner so a network hiccup reads as "loading", not "frozen".
  const [buffering, setBuffering] = useState(false);
  // Fraction (0..1) the browser has buffered ahead — painted under the scrubber.
  const [buffered, setBuffered] = useState(0);
  // While the user is dragging the scrubber we show the dragged position and
  // suppress timeupdate writes so the thumb doesn't fight the playhead.
  const [scrubbing, setScrubbing] = useState(false);

  const currentSlide = slides[currentSlideIndex];
  const currentAudio = audioForSlide(currentSlideIndex);
  const audioUrl = currentAudio?.public_url ?? null;
  const advanceRule = currentSlide?.advance_rule ?? 'manual';

  // Manifest duration is a useful fallback before metadata loads.
  const manifestDuration = useMemo(
    () => (currentAudio?.duration_ms ? currentAudio.duration_ms / 1000 : 0),
    [currentAudio]
  );

  // ---- Seek-on-load (resume) ------------------------------------------------
  // When the parent passes a resume position for this slide, stash it; the
  // 'loadedmetadata' handler applies it once the element knows its duration.
  useEffect(() => {
    seekTargetRef.current =
      resumeSeconds && resumeSeconds > 0 ? resumeSeconds : null;
    // New slide: reset displayed transport.
    setElapsed(0);
    setDuration(manifestDuration);
    setBuffered(0);
    setBuffering(false);
    setScrubbing(false);
  }, [audioUrl, resumeSeconds, manifestDuration]);

  // ---- Keep element playbackRate in sync ------------------------------------
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = playbackRate;
  }, [playbackRate, audioUrl]);

  // ---- Drive play/pause from the store --------------------------------------
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !audioUrl) return;
    if (isPlaying) {
      el.play().catch(() => setPlaying(false));
    } else {
      el.pause();
    }
  }, [isPlaying, audioUrl, setPlaying]);

  // ---- Precache next slide's audio ------------------------------------------
  useEffect(() => {
    const next = audioForSlide(currentSlideIndex + 1);
    const url = next?.public_url;
    if (!url) {
      preloadRef.current = null;
      return;
    }
    const el = new Audio();
    el.preload = 'auto';
    el.src = url;
    // Touch load() so the browser starts buffering ahead of time.
    el.load();
    preloadRef.current = el;
    return () => {
      preloadRef.current = null;
    };
  }, [currentSlideIndex, audioForSlide]);

  // ---- Buffered-range tracking ----------------------------------------------
  const updateBuffered = useCallback(() => {
    const el = audioRef.current;
    if (!el || !el.buffered || el.buffered.length === 0) return;
    const total = el.duration || manifestDuration;
    if (!total || !isFinite(total)) return;
    // Use the buffered range that contains (or is nearest after) the playhead.
    const t = el.currentTime;
    let end = 0;
    for (let i = 0; i < el.buffered.length; i++) {
      if (el.buffered.start(i) <= t + 0.25) end = Math.max(end, el.buffered.end(i));
    }
    if (end === 0) end = el.buffered.end(el.buffered.length - 1);
    setBuffered(Math.max(0, Math.min(1, end / total)));
  }, [manifestDuration]);

  // ---- Audio element event handlers -----------------------------------------
  const handleLoadedMetadata = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    setDuration(isFinite(el.duration) ? el.duration : manifestDuration);
    el.playbackRate = playbackRate;
    if (seekTargetRef.current != null) {
      el.currentTime = Math.min(seekTargetRef.current, el.duration || Infinity);
      seekTargetRef.current = null;
    }
    updateBuffered();
    // Continuous playback after an auto-advance: resume play on the new slide.
    if (autoContinueRef.current) {
      autoContinueRef.current = false;
      setPlaying(true);
    }
  }, [manifestDuration, playbackRate, setPlaying, updateBuffered]);

  const handleTimeUpdate = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    const t = el.currentTime;
    // While the user drags the scrubber, the displayed/elapsed value is owned by
    // the drag handler — don't let the playhead overwrite it.
    if (!scrubbing) setElapsed(t);
    setPosition(t);
    updateBuffered();
    // Throttle persistence to ~once every 5s of wall clock.
    const now = Date.now();
    if (now - lastPersistRef.current > 5000) {
      lastPersistRef.current = now;
      onPersistPosition(currentSlideIndex, Math.floor(t * 1000));
    }
  }, [currentSlideIndex, onPersistPosition, setPosition, scrubbing, updateBuffered]);

  const handleEnded = useCallback(() => {
    setPlaying(false);
    setBuffering(false);
    setElapsed(duration);
    // Persist the final position immediately so a reload lands at slide end.
    onPersistPosition(currentSlideIndex, Math.floor(duration * 1000));
    lastPersistRef.current = Date.now();
    onAudioEnded(currentSlideIndex);
    // Auto-advance only when the slide opts in via advance_rule. Flag the next
    // slide to keep playing so the lecture flows continuously.
    if (advanceRule === 'on_audio_end' && currentSlideIndex < slides.length - 1) {
      autoContinueRef.current = true;
      nextSlide();
    }
  }, [
    advanceRule,
    currentSlideIndex,
    duration,
    nextSlide,
    onAudioEnded,
    onPersistPosition,
    setPlaying,
    slides.length,
  ]);

  const totalDuration = duration || manifestDuration;

  // ---- Imperative seek helper (shared by keyboard + buttons) ----------------
  const seekTo = useCallback(
    (seconds: number) => {
      const el = audioRef.current;
      const clamped = Math.max(0, Math.min(seconds, totalDuration || 0));
      setElapsed(clamped);
      setPosition(clamped);
      if (el) el.currentTime = clamped;
    },
    [totalDuration, setPosition]
  );

  const seekBy = useCallback(
    (delta: number) => seekTo((audioRef.current?.currentTime ?? elapsed) + delta),
    [seekTo, elapsed]
  );

  // ---- Keyboard shortcuts ----------------------------------------------------
  // Space = play/pause · ←/→ = seek ±5s · , / . = prev/next slide · number keys
  // 1–6 select the playback rate. Ignored while typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (
        t.tagName === 'INPUT' ||
        t.tagName === 'TEXTAREA' ||
        t.isContentEditable
      ) {
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault();
          if (audioUrl) setPlaying(!useLectureStore.getState().isPlaying);
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (audioUrl) seekBy(SEEK_STEP);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (audioUrl) seekBy(-SEEK_STEP);
          break;
        case 'l':
          e.preventDefault();
          if (audioUrl) seekBy(SEEK_STEP);
          break;
        case 'j':
          e.preventDefault();
          if (audioUrl) seekBy(-SEEK_STEP);
          break;
        case ',':
          e.preventDefault();
          prevSlide();
          break;
        case '.':
          e.preventDefault();
          nextSlide();
          break;
        default: {
          // Number keys 1–6 → playback rate presets.
          const n = Number(e.key);
          if (Number.isInteger(n) && n >= 1 && n <= PLAYBACK_RATES.length) {
            e.preventDefault();
            setPlaybackRate(PLAYBACK_RATES[n - 1]);
          }
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [audioUrl, nextSlide, prevSlide, setPlaying, setPlaybackRate, seekBy]);

  // ---- Scrubber: precise seek with live drag preview ------------------------
  const handleScrubInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = Number(e.target.value);
    setScrubbing(true);
    setElapsed(t); // preview only; commit on pointer/key up
    // Update the audio element live so the user hears where they are.
    const el = audioRef.current;
    if (el) el.currentTime = t;
  };

  const commitScrub = () => {
    if (!scrubbing) return;
    setScrubbing(false);
    setPosition(elapsed);
    onPersistPosition(currentSlideIndex, Math.floor(elapsed * 1000));
    lastPersistRef.current = Date.now();
  };

  const atFirst = currentSlideIndex <= 0;
  const atLast = currentSlideIndex >= slides.length - 1;
  // "Concept covered" gate: the current slide's audio ended or its widget
  // reported complete. Drives the encouraging Continue affordance below.
  const covered =
    !!currentSlide && completedSlides.includes(currentSlide.id);

  // No audio for this slide: show a quiet "not generated" strip but keep the
  // prev/next controls usable so reading + AI still work.
  if (!audioUrl) {
    return (
      <div className="flex min-w-0 items-center gap-2 border-t border-ws-border bg-ws-surface px-2 py-2.5 sm:gap-3 sm:px-4">
        <NavButtons
          atFirst={atFirst}
          atLast={atLast}
          onPrev={prevSlide}
          onNext={nextSlide}
        />
        <div className="flex min-w-0 flex-1 items-center justify-center gap-2 text-[11px] text-ws-ink-3 sm:text-xs">
          <VolumeX size={14} className="shrink-0" />
          <span className="truncate">Audio not generated for this slide yet</span>
        </div>
        <CoveredAffordance covered={covered} atLast={atLast} onNext={nextSlide} />
        <div className="shrink-0 text-[11px] tabular-nums text-ws-ink-4 sm:text-xs">
          {currentSlideIndex + 1} / {slides.length}
        </div>
      </div>
    );
  }

  const playheadPct = totalDuration ? (elapsed / totalDuration) * 100 : 0;

  return (
    <div className="border-t border-ws-border bg-ws-surface px-2 py-2.5 sm:px-4">
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="auto"
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onProgress={updateBuffered}
        onEnded={handleEnded}
        onWaiting={() => setBuffering(true)}
        onStalled={() => setBuffering(true)}
        onPlaying={() => setBuffering(false)}
        onCanPlay={() => setBuffering(false)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />

      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        <NavButtons
          atFirst={atFirst}
          atLast={atLast}
          onPrev={prevSlide}
          onNext={nextSlide}
        />

        <button
          onClick={() => setPlaying(!isPlaying)}
          className="relative grid size-11 shrink-0 place-items-center rounded-full bg-ws-accent text-white shadow-sm transition-all hover:bg-ws-accent-hover active:scale-95 sm:size-9"
          title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {buffering ? (
            <Loader2 size={18} className="animate-spin sm:size-4" />
          ) : isPlaying ? (
            <Pause size={18} className="sm:size-4" />
          ) : (
            <Play size={18} className="ml-0.5 sm:size-4" />
          )}
        </button>

        <span className="w-8 shrink-0 text-right text-[11px] tabular-nums text-ws-ink-3 sm:w-9 sm:text-xs">
          {fmt(elapsed)}
        </span>

        {/* Scrubber: a layered track shows buffered ahead + played behind the
            native range input, which stays on top for precise pointer/keyboard
            seeking. */}
        <div className="group relative flex min-w-0 flex-1 items-center">
          <div className="pointer-events-none absolute left-0 right-0 top-1/2 h-1.5 -translate-y-1/2 overflow-hidden rounded-full bg-ws-surface-2">
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-ws-ink-4/40"
              style={{ width: `${buffered * 100}%` }}
            />
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-ws-accent"
              style={{ width: `${playheadPct}%` }}
            />
          </div>
          <input
            type="range"
            min={0}
            max={totalDuration || 0}
            step={0.05}
            value={Math.min(elapsed, totalDuration || 0)}
            onChange={handleScrubInput}
            onMouseUp={commitScrub}
            onTouchEnd={commitScrub}
            onKeyUp={commitScrub}
            onBlur={commitScrub}
            className="lecture-scrubber relative z-10 h-1.5 w-full cursor-pointer appearance-none bg-transparent"
            aria-label="Seek"
            aria-valuetext={`${fmt(elapsed)} of ${fmt(totalDuration)}`}
          />
        </div>

        <span className="hidden w-9 shrink-0 text-xs tabular-nums text-ws-ink-3 sm:block">
          {fmt(totalDuration)}
        </span>

        {/* Playback-rate menu */}
        <div className="relative shrink-0">
          <button
            onClick={() => setRateMenuOpen((o) => !o)}
            className="flex h-9 items-center gap-1 rounded-md border border-ws-border bg-ws-surface px-2 text-xs font-medium text-ws-ink-2 transition-colors hover:bg-ws-surface-2 sm:h-8"
            title="Playback speed (1–6)"
            aria-label="Playback speed"
            aria-haspopup="menu"
            aria-expanded={rateMenuOpen}
          >
            <Gauge size={13} className="hidden sm:block" />
            {playbackRate.toFixed(2).replace(/\.?0+$/, '')}×
          </button>
          {rateMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setRateMenuOpen(false)}
              />
              <div
                className="absolute bottom-full right-0 z-20 mb-1 w-52 rounded-md border border-ws-border bg-ws-tile p-3 shadow-lg"
                role="menu"
              >
                {/* Fine speed slider — 5% increments */}
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-ws-ink-2">Speed</span>
                  <span className="text-xs font-semibold tabular-nums text-ws-accent">
                    {playbackRate.toFixed(2).replace(/\.?0+$/, '')}×
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    aria-label="Slower by 5%"
                    onClick={() => setPlaybackRate(playbackRate - RATE_STEP)}
                    className="grid h-6 w-6 shrink-0 place-items-center rounded text-ws-ink-2 transition-colors hover:bg-ws-surface-2"
                  >
                    −
                  </button>
                  <input
                    type="range"
                    min={RATE_MIN}
                    max={RATE_MAX}
                    step={RATE_STEP}
                    value={playbackRate}
                    onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
                    className="lecture-scrubber h-1.5 flex-1 cursor-pointer accent-ws-accent"
                    aria-label="Playback speed"
                    aria-valuetext={`${playbackRate.toFixed(2).replace(/\.?0+$/, '')} times`}
                  />
                  <button
                    type="button"
                    aria-label="Faster by 5%"
                    onClick={() => setPlaybackRate(playbackRate + RATE_STEP)}
                    className="grid h-6 w-6 shrink-0 place-items-center rounded text-ws-ink-2 transition-colors hover:bg-ws-surface-2"
                  >
                    +
                  </button>
                </div>
                {/* Quick presets */}
                <div className="mt-2.5 flex flex-wrap gap-1 border-t border-ws-border pt-2">
                  {PLAYBACK_RATES.map((r) => (
                    <button
                      key={r}
                      role="menuitemradio"
                      aria-checked={r === playbackRate}
                      onClick={() => setPlaybackRate(r)}
                      // WIP-007: min-h-6 clears the 24px WCAG 2.5.8 target-size
                      // floor — text-[11px] + py-0.5 alone rendered shorter.
                      className={`min-h-6 rounded px-1.5 py-0.5 text-[11px] tabular-nums transition-colors hover:bg-ws-surface-2 ${
                        r === playbackRate
                          ? 'font-semibold text-ws-accent'
                          : 'text-ws-ink-2'
                      }`}
                    >
                      {r}×
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* "Concept covered" gate — encouraging affordance to move on once the
            narration finished or the widget was completed. */}
        <CoveredAffordance covered={covered} atLast={atLast} onNext={nextSlide} />
      </div>
    </div>
  );
}

// Subtle "✓ covered" state + a clear, encouraging Continue button. Appears only
// once the slide is covered; on the final slide it shows a quiet completion mark
// instead of a Continue action.
function CoveredAffordance({
  covered,
  atLast,
  onNext,
}: {
  covered: boolean;
  atLast: boolean;
  onNext: () => void;
}) {
  if (!covered) return null;
  if (atLast) {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-ws-success/10 px-2 py-1.5 text-[11px] font-medium text-ws-success sm:py-1 sm:text-xs">
        <Check size={14} className="sm:size-[13px]" />
        <span className="hidden sm:inline">Covered</span>
      </span>
    );
  }
  return (
    <button
      onClick={onNext}
      className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-ws-success-solid px-2 py-2 text-xs font-semibold text-ws-on-success shadow-sm transition-all hover:brightness-95 active:scale-95 sm:px-2.5 sm:py-1.5"
      title="Continue to the next slide (.)"
      aria-label="Continue to the next slide"
    >
      <Check size={16} className="sm:size-[13px]" />
      <span className="hidden sm:inline">Continue</span>
      <ArrowRight size={16} className="sm:size-[13px]" />
    </button>
  );
}

function NavButtons({
  atFirst,
  atLast,
  onPrev,
  onNext,
}: {
  atFirst: boolean;
  atLast: boolean;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className="flex shrink-0 items-center gap-0.5 sm:gap-1">
      <button
        onClick={onPrev}
        disabled={atFirst}
        className="grid size-10 place-items-center rounded-md text-ws-ink-2 transition-colors hover:bg-ws-surface-2 disabled:opacity-40 sm:size-8"
        title="Previous slide (,)"
        aria-label="Previous slide"
      >
        <SkipBack size={16} />
      </button>
      <button
        onClick={onNext}
        disabled={atLast}
        className="grid size-10 place-items-center rounded-md text-ws-ink-2 transition-colors hover:bg-ws-surface-2 disabled:opacity-40 sm:size-8"
        title="Next slide (.)"
        aria-label="Next slide"
      >
        <SkipForward size={16} />
      </button>
    </div>
  );
}
